import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Formulario, type DefCampo } from '../../componentes/formulario';
import { anunciarOcultos } from '../../componentes/sensivel';
import { useToast } from '../../componentes/toast';
import { cabecalhoEmpresa, get, mensagemDeErro, patch, post } from '../../nucleo/api';
import { campoEmpresaDoRegistro, empresaEscolhida } from '../../nucleo/empresa/empresa-do-registro';
import { data, dinheiro, hojeIso, mesAno, nomeParceiro, nomePessoa, simboloMoeda } from '../../nucleo/formato';
import { exemploDinheiroEm } from '../../nucleo/mascaras';
import { useSessao } from '../../nucleo/sessao';
import { invalidarAssinaturas, useOpcoesContrato } from './dados';
import { itemDoContrato, itensParaApi, ItensContrato, novoItem, quantidadeDe, type ItemForm } from './ItensContrato';
import { CAMINHO_CONTRATOS, CAMINHO_PLANOS, FINS_CONTRATO, INDICES, INTERVALOS, mesesDoCiclo, MOMENTOS, mrrDaTela, previaDoCalendario, TIPOS_CONTRATO, UNIDADES, valorDaTela, type Contrato, type Plano, type Unidade } from './tipos';
import { useEmpresasDoCatalogo } from '../../componentes/verticais';

interface Resposta {
  id: string;
  numero?: string;
  faturamento?: { emitidas: number; falhas: unknown[] };
}

const inteiro = (v: unknown) => (v === '' || v === null || v === undefined ? null : Number(v));

/** C4: "repete a cada" da tela (atalho, número e unidade) no intervalo e na unidade da API. */
export function recorrenciaDaTela(v: Record<string, unknown>): { intervalo: number; unidade: Unidade } {
  if (v.tipo === 'avulso') return { intervalo: 1, unidade: 'mes' };
  if (v.intervalo === 'semanal') return { intervalo: 1, unidade: 'semana' };
  if (v.intervalo === 'outro') return { intervalo: Number(v.intervalo_meses) || 1, unidade: UNIDADES.includes(v.unidade_recorrencia as Unidade) ? (v.unidade_recorrencia as Unidade) : 'mes' };
  return { intervalo: Number(v.intervalo) || 1, unidade: 'mes' };
}

/** C4: atalho da tela para o contrato gravado (mensal, trimestral, semestral, anual, semanal ou outro). */
export function atalhoDoContrato(c: Pick<Contrato, 'intervalo_meses' | 'unidade_recorrencia'>): string {
  if (c.unidade_recorrencia === 'semana' && c.intervalo_meses === 1) return 'semanal';
  if ((c.unidade_recorrencia ?? 'mes') === 'mes' && INTERVALOS.includes(c.intervalo_meses as never)) return String(c.intervalo_meses);
  return 'outro';
}

/**
 * Formulário do contrato (W12.2 a W12.5): cabeçalho, itens, totais e a prévia das próximas cobranças. O id nasce aqui:
 * reenviar o mesmo formulário (clique duplo, nova tentativa) não cria um segundo contrato.
 * C4 (lote de 09/10): as quatro áreas do antigo, com os nomes dele: Identificação, Serviços do contrato (itens, desconto e
 * totais), Tipo e vigência (com a prévia) e Pagamento; campos personalizados ao fim, como em todo formulário. Centro de
 * custo do contrato como padrão dos itens e "repete a cada" por dia, semana ou mês.
 */
export function FormContrato({ contrato, idForm, aoSalvar }: { contrato: Contrato | null; idForm: string; aoSalvar: (id: string) => void }) {
  const { t } = useTranslation('assinaturas');
  const { t: tn } = useTranslation();
  const s = useSessao();
  const toast = useToast();
  const qc = useQueryClient();
  const opcoes = useOpcoesContrato();
  const [novoId] = useState(() => crypto.randomUUID());
  const [itens, setItens] = useState<ItemForm[]>(contrato ? contrato.itens.map(itemDoContrato) : [novoItem()]);
  // C4: trocar o centro do contrato leva o novo aos itens sem centro ou com o centro anterior do contrato.
  const centroAnterior = useRef<string>(contrato?.centro_custo_id ?? '');
  const catalogoEmpresas = useEmpresasDoCatalogo();
  const [moedaDoPlano, setMoedaDoPlano] = useState<string | null>(null);
  const podeEditar = contrato ? s.pode('assinaturas.editar') : s.pode('assinaturas.criar');
  const [valores, setValores] = useState<Record<string, unknown>>({});
  const o = opcoes.data;

  const empresaId = contrato ? contrato.empresa_id : s.empresas?.atual;
  const empresaNome = s.empresas?.opcoes.find((e) => e.id === empresaId)?.nome ?? '';
  const inicial: Record<string, unknown> = contrato
    ? {
        _empresa: empresaNome,
        _numero: contrato.numero,
        plano_id: contrato.plano_id ?? '',
        centro_custo_id: contrato.centro_custo_id ?? '',
        parceiro_id: contrato.parceiro_id,
        vendedor_id: contrato.vendedor_id ?? '',
        referencia_externa: contrato.referencia_externa ?? '',
        tipo: contrato.tipo,
        inicio: contrato.inicio,
        primeira_cobranca: contrato.primeira_cobranca,
        intervalo: atalhoDoContrato(contrato),
        intervalo_meses: contrato.intervalo_meses,
        unidade_recorrencia: contrato.unidade_recorrencia ?? 'mes',
        fim_tipo: contrato.fim_tipo,
        fim_data: contrato.fim_data ?? '',
        fim_cobrancas: contrato.fim_cobrancas,
        dia_geracao: contrato.dia_geracao,
        dia_vencimento: contrato.dia_vencimento,
        momento_pagamento: contrato.momento_pagamento,
        forma_pagamento_id: contrato.forma_pagamento_id ?? '',
        condicao_pagamento_id: contrato.condicao_pagamento_id ?? '',
        desconto_tipo: contrato.desconto_percentual > 0 ? 'percentual' : contrato.desconto_centavos > 0 ? 'valor' : 'nenhum',
        desconto_centavos: contrato.desconto_centavos,
        desconto_percentual: contrato.desconto_percentual,
        indice: contrato.indice,
        observacoes_fatura: contrato.observacoes_fatura ?? '',
        extras: contrato.extras,
      }
    : {
        _empresa: empresaNome,
        _numero: '',
        plano_id: '',
        centro_custo_id: '',
        tipo: 'recorrente',
        inicio: hojeIso(),
        primeira_cobranca: hojeIso(),
        intervalo: '1',
        intervalo_meses: 1,
        unidade_recorrencia: 'mes',
        fim_tipo: 'indeterminado',
        dia_geracao: 1,
        momento_pagamento: 'vigente',
        desconto_tipo: 'nenhum',
        indice: 'nenhum',
      };

  const recorrente = (v: Record<string, unknown>) => v.tipo !== 'avulso';
  // Por dia ou semana a fatura nasce na data de cada ciclo: dia de geração e de vencimento (dias do mês) não se aplicam.
  const porMes = (v: Record<string, unknown>) => recorrenciaDaTela(v).unidade === 'mes';
  const ID = t('areas.identificacao');
  const SERVICOS = t('areas.servicos');
  const VIGENCIA = t('areas.vigencia');
  const PAGAMENTO = t('areas.pagamento');
  const campos: DefCampo[] = [
    // Identificação
    // V2 verticais: contrato novo sem empresa na aba (sem filtro, várias permitidas) escolhe a empresa; senão ela só aparece.
    ...(!contrato ? campoEmpresaDoRegistro(s.empresas, { rotulo: t('campos.empresa'), ajuda: t('ajuda.empresa') }, { grupo: ID }) : []),
    ...(s.empresas && empresaNome ? [{ nome: '_empresa', rotulo: t('campos.empresa'), tipo: 'texto' as const, largura: 'meia' as const, grupo: ID, desabilitado: true, ajuda: contrato ? undefined : t('ajuda.empresa') }] : []),
    ...(o?.centros
      ? [{ nome: 'centro_custo_id', rotulo: t('campos.centroCusto'), tipo: 'selecao' as const, largura: 'meia' as const, grupo: ID, desabilitado: !podeEditar, opcoes: o.centros, ajuda: t('ajuda.centroCusto') }]
      : []),
    { nome: '_numero', rotulo: t('campos.numero'), tipo: 'texto', largura: 'meia', grupo: ID, desabilitado: true, placeholder: t('ajuda.numeroAoSalvar') },
    // Pacote 2: plano de origem. No contrato novo, escolher o plano traz os itens e a recorrência dele (tudo editável).
    ...(o?.planos.length || contrato?.plano_id
      ? [
          {
            nome: 'plano_id',
            rotulo: t('campos.plano'),
            tipo: 'selecao' as const,
            largura: 'meia' as const,
            grupo: ID,
            desabilitado: !podeEditar,
            ajuda: contrato ? t('ajuda.planoEdicao') : t('ajuda.plano'),
            opcoes: [...(o?.planos ?? []), ...(contrato?.plano_id && !o?.planos.some((p) => p.valor === contrato.plano_id) ? [{ valor: contrato.plano_id, rotulo: contrato.plano_nome ?? t('campos.planoInativo') }] : [])],
          },
        ]
      : []),
    {
      nome: 'parceiro_id',
      rotulo: t('campos.cliente'),
      tipo: 'busca',
      obrigatorio: true,
      largura: 'meia',
      grupo: ID,
      desabilitado: !podeEditar,
      busca: { caminho: '/cadastros/parceiros', params: { papel: 'cliente', operacao: true }, rotulo: (i) => nomePessoa(String(i.nome)) },
    },
    { nome: 'vendedor_id', rotulo: t('campos.vendedor'), tipo: 'busca', largura: 'meia', grupo: ID, desabilitado: !podeEditar, busca: { caminho: '/membros', rotulo: (i) => String(i.nome) } },
    { nome: 'referencia_externa', rotulo: t('campos.referencia'), tipo: 'texto', largura: 'meia', grupo: ID, ajuda: t('ajuda.referencia'), desabilitado: !podeEditar },
    // Serviços do contrato: os itens entram antes do desconto (inicioDoGrupo) e os totais depois (fimDoGrupo).
    {
      nome: 'desconto_tipo',
      campoTela: 'desconto',
      rotulo: t('campos.descontoTipo'),
      tipo: 'selecao',
      largura: 'terco',
      grupo: SERVICOS,
      desabilitado: !podeEditar,
      opcoes: (['nenhum', 'valor', 'percentual'] as const).map((x) => ({ valor: x, rotulo: t(`descontoTipos.${x}`) })),
    },
    { nome: 'desconto_centavos', campoTela: 'desconto', rotulo: t('campos.desconto'), tipo: 'dinheiro', largura: 'terco', grupo: SERVICOS, visivel: (v) => v.desconto_tipo === 'valor', desabilitado: !podeEditar },
    { nome: 'desconto_percentual', campoTela: 'desconto', rotulo: t('campos.descontoPct'), tipo: 'percentual', largura: 'terco', grupo: SERVICOS, min: 0, max: 100, passo: 0.01, visivel: (v) => v.desconto_tipo === 'percentual', desabilitado: !podeEditar },
    // Tipo e vigência
    { nome: 'tipo', rotulo: t('campos.tipo'), tipo: 'selecao', obrigatorio: true, largura: 'meia', grupo: VIGENCIA, desabilitado: !podeEditar, opcoes: TIPOS_CONTRATO.map((x) => ({ valor: x, rotulo: t(`tipos.${x}`) })) },
    { nome: 'fim_tipo', rotulo: t('campos.fimTipo'), tipo: 'selecao', largura: 'meia', grupo: VIGENCIA, visivel: recorrente, desabilitado: !podeEditar, opcoes: FINS_CONTRATO.map((x) => ({ valor: x, rotulo: t(`fins.${x}`) })) },
    { nome: 'inicio', rotulo: t('campos.inicio'), tipo: 'data', obrigatorio: true, largura: 'meia', grupo: VIGENCIA, desabilitado: !podeEditar },
    { nome: 'primeira_cobranca', rotulo: t('campos.primeiraCobranca'), tipo: 'data', obrigatorio: true, largura: 'meia', grupo: VIGENCIA, ajuda: t('ajuda.primeiraCobranca'), desabilitado: !podeEditar },
    { nome: 'fim_data', rotulo: t('campos.fimData'), tipo: 'data', largura: 'meia', grupo: VIGENCIA, visivel: (v) => recorrente(v) && v.fim_tipo === 'data', desabilitado: !podeEditar },
    { nome: 'fim_cobrancas', rotulo: t('campos.fimCobrancas'), tipo: 'numero', largura: 'meia', grupo: VIGENCIA, min: 1, max: 600, passo: 1, visivel: (v) => recorrente(v) && v.fim_tipo === 'cobrancas', desabilitado: !podeEditar },
    { nome: 'indice', rotulo: t('campos.indice'), tipo: 'selecao', largura: 'meia', grupo: VIGENCIA, ajuda: t('ajuda.indice'), visivel: recorrente, desabilitado: !podeEditar, opcoes: INDICES.map((x) => ({ valor: x, rotulo: t(`indices.${x}`) })) },
    { nome: 'dia_geracao', rotulo: t('campos.diaGeracao'), tipo: 'numero', obrigatorio: true, largura: 'meia', grupo: VIGENCIA, min: 1, max: 31, passo: 1, ajuda: t('ajuda.diaGeracao'), visivel: porMes, desabilitado: !podeEditar },
    { nome: 'dia_vencimento', rotulo: t('campos.diaVencimento'), tipo: 'numero', largura: 'meia', grupo: VIGENCIA, min: 1, max: 31, passo: 1, ajuda: t('ajuda.diaVencimento'), visivel: porMes, desabilitado: !podeEditar },
    { nome: 'momento_pagamento', rotulo: t('campos.momento'), tipo: 'selecao', largura: 'meia', grupo: VIGENCIA, ajuda: t('ajuda.momento'), desabilitado: !podeEditar, opcoes: MOMENTOS.map((x) => ({ valor: x, rotulo: t(`momentos.${x}`) })) },
    {
      nome: 'intervalo',
      rotulo: t('campos.repete'),
      tipo: 'selecao',
      largura: 'meia',
      grupo: VIGENCIA,
      visivel: recorrente,
      desabilitado: !podeEditar,
      opcoes: [...INTERVALOS.map((n) => ({ valor: String(n), rotulo: t(`intervalos.${n}`) })), { valor: 'semanal', rotulo: t('intervalos.semanal') }, { valor: 'outro', rotulo: t('intervalos.outro') }],
    },
    { nome: 'intervalo_meses', rotulo: t('campos.intervaloMeses'), tipo: 'numero', largura: 'meia', grupo: VIGENCIA, min: 1, max: 60, passo: 1, visivel: (v) => recorrente(v) && v.intervalo === 'outro', desabilitado: !podeEditar },
    {
      nome: 'unidade_recorrencia',
      rotulo: t('campos.unidade'),
      tipo: 'selecao',
      largura: 'meia',
      grupo: VIGENCIA,
      ajuda: t('ajuda.unidade'),
      visivel: (v) => recorrente(v) && v.intervalo === 'outro',
      desabilitado: !podeEditar,
      opcoes: UNIDADES.map((x) => ({ valor: x, rotulo: t(`unidades.${x}`) })),
    },
    // Pagamento
    ...(o?.formas ? [{ nome: 'forma_pagamento_id', rotulo: t('campos.forma'), tipo: 'selecao' as const, largura: 'meia' as const, grupo: PAGAMENTO, desabilitado: !podeEditar, opcoes: o.formas }] : []),
    ...(o?.condicoes ? [{ nome: 'condicao_pagamento_id', rotulo: t('campos.condicao'), tipo: 'selecao' as const, largura: 'meia' as const, grupo: PAGAMENTO, desabilitado: !podeEditar, opcoes: o.condicoes }] : []),
    { nome: 'observacoes_fatura', rotulo: t('campos.observacoes'), tipo: 'area', grupo: PAGAMENTO, desabilitado: !podeEditar },
  ];

  // Totais e prévia do que está na tela (o servidor recalcula e é quem vale).
  const v = { ...inicial, ...valores };
  // Lista de 04/11, item 3 (D2-A): a moeda do contrato novo é a do plano; sem plano, a da empresa dele; sem ela, a do workspace (a regra do servidor).
  const empresaDoContrato = empresaEscolhida(v) ?? s.empresas?.atual ?? null;
  const moeda = contrato?.moeda ?? moedaDoPlano ?? (empresaDoContrato ? catalogoEmpresas.data?.empresas.find((e) => e.id === empresaDoContrato)?.moeda : null) ?? s.workspace.moeda;
  const { intervalo, unidade } = recorrenciaDaTela(v);
  const centroContrato = String(v.centro_custo_id ?? '');
  useEffect(() => {
    const antes = centroAnterior.current;
    if (antes === centroContrato) return;
    centroAnterior.current = centroContrato;
    setItens((l) => l.map((i) => (!i.centro_custo_id || i.centro_custo_id === antes ? { ...i, centro_custo_id: centroContrato } : i)));
  }, [centroContrato]);
  // Pacote 2: o plano escolhido no contrato novo traz os itens (com o custo e os padrões do serviço) e a recorrência.
  const planoEscolhido = String(v.plano_id ?? '');
  const planoAnterior = useRef(contrato?.plano_id ?? '');
  const [sobrepor, setSobrepor] = useState<Record<string, unknown> | undefined>(undefined);
  useEffect(() => {
    if (contrato || planoEscolhido === planoAnterior.current) return;
    planoAnterior.current = planoEscolhido;
    setMoedaDoPlano(null);
    if (!planoEscolhido) return;
    let vivo = true;
    get<Plano>(`${CAMINHO_PLANOS}/${planoEscolhido}`)
      .then((p) => {
        if (!vivo) return;
        setMoedaDoPlano(p.moeda);
        setItens(
          p.itens.map((i) => {
            const sv = o?.servicos.find((x) => x.id === i.servico_id);
            return {
              chave: crypto.randomUUID(),
              servico_id: i.servico_id ?? '',
              descricao: i.descricao,
              quantidade: String(i.quantidade).replace('.', ','),
              preco: i.preco_centavos,
              custo: sv?.custo_centavos ?? null,
              categoria_id: sv?.categoria_padrao_id ?? '',
              centro_custo_id: centroContrato || sv?.centro_custo_padrao_id || '',
              recorrencia: '',
            };
          }),
        );
        setSobrepor({ tipo: p.tipo, intervalo: atalhoDoContrato(p), intervalo_meses: p.intervalo_meses, unidade_recorrencia: p.unidade_recorrencia });
      })
      .catch((e) => toast.erro(mensagemDeErro(e)));
    return () => {
      vivo = false;
    };
  }, [planoEscolhido]); // eslint-disable-line react-hooks/exhaustive-deps
  const totais = valorDaTela(
    itens.map((i) => ({ quantidade: quantidadeDe(i.quantidade), preco_centavos: i.preco ?? 0 })),
    v.desconto_tipo === 'valor' ? Number(v.desconto_centavos) || 0 : 0,
    v.desconto_tipo === 'percentual' ? Number(v.desconto_percentual) || 0 : 0,
  );
  const previa = previaDoCalendario({
    tipo: String(v.tipo),
    primeira_cobranca: String(v.primeira_cobranca ?? ''),
    intervalo_meses: intervalo,
    unidade_recorrencia: unidade,
    fim_tipo: String(v.fim_tipo ?? 'indeterminado'),
    fim_data: (v.fim_data as string) || null,
    fim_cobrancas: inteiro(v.fim_cobrancas),
    dia_geracao: Number(v.dia_geracao) || 1,
    momento_pagamento: String(v.momento_pagamento ?? 'vigente'),
    dia_vencimento: inteiro(v.dia_vencimento),
  }).slice(0, 6);

  const itensDoContrato = (
    <ItensContrato
      itens={itens}
      aoMudar={setItens}
      servicos={o?.servicos ?? []}
      categorias={o?.categorias ?? null}
      centros={o?.centros ?? null}
      centroPadrao={centroContrato}
      moeda={moeda}
      desabilitado={!podeEditar}
      comRecorrencia
    />
  );
  const totaisDoContrato = (
    <dl className="grid min-w-0 grid-cols-2 gap-3 rounded-lg border border-borda bg-sup-1 p-3 text-sm sm:col-span-6 sm:grid-cols-4">
      {(
        [
          ['bruto', totais.bruto],
          ['desconto', totais.desconto],
          ['liquido', totais.liquido],
          [
            'mrr',
            v.tipo === 'avulso'
              ? null
              : mrrDaTela(
                  itens.map((i) => ({ quantidade: quantidadeDe(i.quantidade), preco_centavos: i.preco ?? 0, recorrencia: i.recorrencia })),
                  v.desconto_tipo === 'valor' ? Number(v.desconto_centavos) || 0 : 0,
                  v.desconto_tipo === 'percentual' ? Number(v.desconto_percentual) || 0 : 0,
                  mesesDoCiclo(intervalo, unidade),
                ),
          ],
        ] as const
      ).map(([k, valor]) => (
        <div key={k} className="min-w-0">
          <dt className="text-xs text-tinta-3">{t(`itens.${k}`)}</dt>
          <dd className="numero font-semibold text-tinta-1">{valor === null ? '' : anunciarOcultos(dinheiro(valor, moeda))}</dd>
        </div>
      ))}
    </dl>
  );
  const previaDoContrato =
    previa.length > 0 ? (
      <div className="min-w-0 sm:col-span-6">
        <p className="mb-1 text-sm font-medium text-tinta-2">{t('detalhe.previsao')}</p>
        <ul className="flex flex-col gap-1 text-sm">
          {previa.map((p) => (
            <li key={p.periodo ?? p.competencia} className="flex flex-wrap gap-x-4 gap-y-0.5 rounded-lg border border-borda px-3 py-2">
              <span className="numero font-medium text-tinta-1">{p.periodo ? data(p.periodo) : mesAno(p.competencia)}</span>
              <span className="text-tinta-3">
                {t('campos.geracao')}: <span className="numero text-tinta-2">{data(p.data_geracao)}</span>
              </span>
              <span className="text-tinta-3">
                {t('campos.vencimento')}: <span className="numero text-tinta-2">{data(p.vencimento)}</span>
              </span>
            </li>
          ))}
        </ul>
      </div>
    ) : null;

  return (
    <Formulario
      id={idForm}
      // rl0411-2 (SPEC-M4): o exemplo do desconto segue a moeda do contrato, calculada depois dos campos.
      campos={campos.map((c) => (c.nome === 'desconto_centavos' ? { ...c, placeholder: exemploDinheiroEm(simboloMoeda(moeda)) } : c))}
      telaCampos="assinaturas.contrato"
      entidade="assinaturas.contrato"
      inicial={inicial}
      rotulosAtuais={contrato ? { parceiro_id: nomeParceiro(contrato.parceiro_nome) } : undefined}
      aoMudar={setValores}
      sobrepor={sobrepor}
      inicioDoGrupo={{ [SERVICOS]: itensDoContrato }}
      fimDoGrupo={{ [SERVICOS]: totaisDoContrato, [VIGENCIA]: previaDoContrato }}
      aoEnviar={async (f) => {
        const itensApi = itensParaApi(itens, t('itens.incompleto'));
        const tipo = f.tipo as string;
        const rec = recorrenciaDaTela(f);
        const corpo: Record<string, unknown> = {
          parceiro_id: f.parceiro_id,
          vendedor_id: f.vendedor_id || null,
          referencia_externa: String(f.referencia_externa ?? '').trim() || null,
          tipo,
          inicio: f.inicio,
          primeira_cobranca: f.primeira_cobranca || null,
          intervalo_meses: rec.intervalo,
          unidade_recorrencia: rec.unidade,
          fim_tipo: tipo === 'avulso' ? 'indeterminado' : f.fim_tipo,
          fim_data: f.fim_tipo === 'data' ? f.fim_data || null : null,
          fim_cobrancas: f.fim_tipo === 'cobrancas' ? inteiro(f.fim_cobrancas) : null,
          dia_geracao: Number(f.dia_geracao) || 1,
          dia_vencimento: inteiro(f.dia_vencimento),
          momento_pagamento: f.momento_pagamento,
          desconto_centavos: f.desconto_tipo === 'valor' ? Number(f.desconto_centavos) || 0 : 0,
          desconto_percentual: f.desconto_tipo === 'percentual' ? Number(f.desconto_percentual) || 0 : 0,
          indice: tipo === 'avulso' ? 'nenhum' : f.indice,
          observacoes_fatura: String(f.observacoes_fatura ?? '').trim() || null,
          extras: f.extras ?? {},
          itens: itensApi,
        };
        if (o?.planos.length || contrato?.plano_id) corpo.plano_id = f.plano_id || null;
        // Sem acesso ao Cadastros ou ao Financeiro as listas nem aparecem: o que já estava gravado não é tocado.
        if (o?.formas) corpo.forma_pagamento_id = f.forma_pagamento_id || null;
        if (o?.condicoes) corpo.condicao_pagamento_id = f.condicao_pagamento_id || null;
        if (o?.centros) corpo.centro_custo_id = f.centro_custo_id || null;
        if (contrato) {
          await patch(`${CAMINHO_CONTRATOS}/${contrato.id}`, corpo);
          await invalidarAssinaturas(qc);
          toast.sucesso(tn('comum.salvo'));
          aoSalvar(contrato.id);
          return;
        }
        const r = await post<Resposta>(CAMINHO_CONTRATOS, { id: novoId, ...corpo }, { 'Idempotency-Key': novoId, ...cabecalhoEmpresa(empresaEscolhida(f)) });
        await invalidarAssinaturas(qc);
        toast.sucesso(t('mensagens.criado', { numero: r.numero ?? '' }));
        const falhas = r.faturamento?.falhas.length ?? 0;
        if (falhas) toast.erro(t('mensagens.comFalha', { count: falhas }));
        else if (r.faturamento?.emitidas) toast.sucesso(t('mensagens.faturado', { count: r.faturamento.emitidas }));
        aoSalvar(r.id);
      }}
    />
  );
}
